import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskPriority, TaskStatus } from './tasksApi';
import type { TaskStatusCategory } from './taskStatusesApi';
import type { UserPermissionMatrix } from './permissionsApi';

// --- Types (mirror backend src/lib/people.ts) ------------------------------

export type ProjectStatus =
  | 'PLANNING'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'ALMOST_DONE'
  | 'LEAD'
  | 'IN_HOUSE';

export interface PublicProjectRef {
  id: string;
  name: string;
  status: ProjectStatus;
}

export interface Allocation {
  id: string;
  projectId: string;
  project: PublicProjectRef;
  // Weekly hours the person spends on this project (CLE-444).
  hours: number;
  startDate: string;
  endDate: string | null;
}

export interface Person {
  id: string;
  userId: string | null;
  fullName: string;
  email: string;
  phone: string | null;
  title: string | null;
  /** Legacy denormalized department name (kept in sync with departmentId). */
  department: string | null;
  /** Structured department reference (configurable departments). */
  departmentId: string | null;
  location: string | null;
  avatarUrl: string | null;
  capacity: number;
  /** First working day on the team (ISO), null when unknown. */
  startDate: string | null;
  // Org chart (CLE-70): id of the person this member reports to, null at the top.
  managerId: string | null;
  utilizationPercent: number;
  allocatedHours: number;
  isOverallocated: boolean;
  isActive: boolean;
  allocations: Allocation[];
  /** Services nomenclature: attached services (present only on detail responses). */
  services?: PersonServiceRef[];
  /** Linked login account (present only on detail responses; null = no account). */
  account?: PersonAccount | null;
  createdAt: string;
  updatedAt: string;
}

export type UserRole = 'ADMIN' | 'MANAGER' | 'MEMBER' | 'VIEWER';

/** The person's linked login account, as exposed by GET /api/people/:id. */
export interface PersonAccount {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt: string | null;
  emailVerifiedAt: string | null;
  /** When the person asked for the account to be deleted (Profil); null otherwise. */
  deletionRequestedAt?: string | null;
}

/** A service tag attached to the person (services nomenclature). */
export interface PersonServiceRef {
  id: string;
  name: string;
  color: string | null;
  category: string;
}

// --- Org chart (CLE-70) ----------------------------------------------------

export interface OrgChartPerson {
  id: string;
  fullName: string;
  email: string;
  title: string | null;
  department: string | null;
  avatarUrl: string | null;
  managerId: string | null;
}

export interface OrgChartNode extends OrgChartPerson {
  reports: OrgChartNode[];
}

export interface OrgChart {
  roots: OrgChartNode[];
  unassigned: OrgChartPerson[];
  total: number;
}

export interface CapacityWeekProject {
  projectId: string;
  projectName: string;
  percentage: number;
  hours: number;
}

export interface CapacityWeek {
  weekStart: string;
  weekEnd: string;
  allocations: CapacityWeekProject[];
  totalPercent: number;
  totalHours: number;
  utilizationPercent: number;
  isOverallocated: boolean;
}

export interface PersonCapacity {
  person: { id: string; fullName: string; capacity: number };
  weeks: CapacityWeek[];
}

export interface AllocationConflict {
  personId: string;
  personName: string;
  weekStart: string;
  weekEnd: string;
  totalPercent: number;
  overBy: number;
  allocations: CapacityWeekProject[];
}

interface Envelope<T> {
  success: true;
  data: T;
}

// Attach the current access token from the auth store to every request.
function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface PeopleFilters {
  department?: string;
  departmentId?: string;
  search?: string;
  active?: 'true' | 'false';
}

export async function listPeople(filters: PeopleFilters = {}): Promise<Person[]> {
  const params = new URLSearchParams();
  if (filters.department) params.set('department', filters.department);
  if (filters.departmentId) params.set('departmentId', filters.departmentId);
  if (filters.search) params.set('search', filters.search);
  if (filters.active) params.set('active', filters.active);
  const qs = params.toString();
  const res = await apiFetch<Envelope<Person[]>>(`/api/people${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getPerson(id: string): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getPersonAllocations(id: string): Promise<Allocation[]> {
  const res = await apiFetch<Envelope<Allocation[]>>(`/api/people/${id}/allocations`, {
    headers: authHeaders(),
  });
  return res.data;
}

export interface CreatePersonInput {
  fullName: string;
  email: string;
  title?: string;
  department?: string;
  location?: string;
  phone?: string;
  capacity?: number;
}

export async function createPerson(input: CreatePersonInput): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>('/api/people', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// --- Team member creation with account + RBAC (CLE-214 / CLE-215) ----------

/**
 * Full POST /api/people payload. Extends the basic person fields with optional
 * login-account provisioning (`createAccount`), an auto/explicit password, a
 * welcome-email flag and the RBAC selection (a role template id and/or a custom
 * permission matrix). Mirrors backend `CreateTeamMemberInput`.
 */
export interface CreateTeamMemberInput {
  fullName: string;
  email: string;
  phone?: string | null;
  title?: string | null;
  department?: string | null;
  /** Structured department id (CLE-DASH); the backend keeps the name mirror in sync. */
  departmentId?: string | null;
  location?: string | null;
  capacity?: number;
  hourlyRate?: number | null;
  reportsToId?: string | null;
  startDate?: string | null;
  avatarUrl?: string | null;
  createAccount: boolean;
  /**
   * How the account gets its credentials: `invite` sends an email link where
   * the user sets their own password; `password` uses the admin-set/generated
   * one (the only option when SMTP is not configured).
   */
  accountMode?: 'invite' | 'password';
  password?: string;
  sendWelcomeEmail: boolean;
  roleTemplateId?: string | null;
  customPermissions?: UserPermissionMatrix | null;
}

/** Welcome-email outcome from the backend (mirrors `WelcomeEmailResult`). */
export interface WelcomeEmailResult {
  sent: boolean;
  smtpNotConfigured: boolean;
  messageId?: string;
}

/** Response of POST /api/people (mirrors the route's `ok({...})` body). */
export interface CreateTeamMemberResult {
  person: Person | null;
  userId: string | null;
  /**
   * Raw password to surface to the admin. Non-null only when no welcome email
   * was delivered (SMTP unconfigured or the admin opted out) — show it in a
   * copy-password dialog in that case.
   */
  generatedPassword: string | null;
  email: WelcomeEmailResult | null;
  /** True when an invite email flow was used instead of a direct password. */
  invited: boolean;
}

export async function createTeamMember(
  input: CreateTeamMemberInput,
): Promise<CreateTeamMemberResult> {
  const res = await apiFetch<Envelope<CreateTeamMemberResult>>('/api/people', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** POST /api/people/:id/resend-welcome — regenerate password + resend email. */
export async function resendWelcome(personId: string): Promise<{
  userId: string;
  generatedPassword: string | null;
  email: WelcomeEmailResult;
}> {
  const res = await apiFetch<
    Envelope<{ userId: string; generatedPassword: string | null; email: WelcomeEmailResult }>
  >(`/api/people/${personId}/resend-welcome`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/people/:id/resend-invite — re-issue the invite link (no password rotation). */
export async function resendInvite(
  personId: string,
): Promise<{ userId: string; email: WelcomeEmailResult }> {
  const res = await apiFetch<Envelope<{ userId: string; email: WelcomeEmailResult }>>(
    `/api/people/${personId}/resend-invite`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

export interface CapacityWindow {
  startDate?: string;
  weeks?: number;
}

export async function getCapacity(window: CapacityWindow = {}): Promise<PersonCapacity[]> {
  const params = new URLSearchParams();
  if (window.startDate) params.set('startDate', window.startDate);
  if (window.weeks) params.set('weeks', String(window.weeks));
  const qs = params.toString();
  const res = await apiFetch<Envelope<PersonCapacity[]>>(
    `/api/people/capacity${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getConflicts(window: CapacityWindow = {}): Promise<AllocationConflict[]> {
  const params = new URLSearchParams();
  if (window.startDate) params.set('startDate', window.startDate);
  if (window.weeks) params.set('weeks', String(window.weeks));
  const qs = params.toString();
  const res = await apiFetch<Envelope<AllocationConflict[]>>(
    `/api/people/conflicts${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  );
  return res.data;
}

// --- Cross-people allocation listing (calendar / team Gantt) ----------------

/** An allocation row from GET /api/allocations, with its person summary. */
export interface TeamAllocation extends Allocation {
  person: { id: string; fullName: string; capacity: number };
}

export interface AllocationsWindow {
  from?: string;
  to?: string;
}

/** GET /api/allocations?from&to — allocations of active people overlapping the window. */
export async function listAllocations(window: AllocationsWindow = {}): Promise<TeamAllocation[]> {
  const params = new URLSearchParams();
  if (window.from) params.set('from', window.from);
  if (window.to) params.set('to', window.to);
  const qs = params.toString();
  const res = await apiFetch<Envelope<TeamAllocation[]>>(`/api/allocations${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export interface CreateAllocationInput {
  personId: string;
  projectId: string;
  hours: number;
  startDate: string;
  endDate?: string;
}

export async function createAllocation(input: CreateAllocationInput): Promise<Allocation> {
  const res = await apiFetch<Envelope<Allocation>>('/api/allocations', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// --- Person task workload (CLE-155) ----------------------------------------
// Mirrors backend src/lib/personTasks.ts.

export interface PersonTaskLabel {
  id: string;
  name: string;
  color: string | null;
}

export interface PersonTask {
  id: string;
  identifier: string | null;
  title: string;
  /** Stable status slug (configurable statuses). */
  status: TaskStatus;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
  priority: TaskPriority;
  priorityValue: number;
  dueDate: string | null;
  labels: PersonTaskLabel[];
  url: string | null;
  projectId: string | null;
  projectName: string | null;
  projectCode: string | null;
}

export interface PersonProjectAllocation {
  projectId: string | null;
  projectName: string;
  projectCode: string | null;
  status: string | null;
  health: string | null;
  role: string | null;
  activeTaskCount: number;
}

export interface PersonTaskStats {
  totalActive: number;
  overdue: number;
  inProgress: number;
  activeProjects: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface PersonTasksResult {
  source: 'local';
  tasks: PersonTask[];
  allocations: PersonProjectAllocation[];
  stats: PersonTaskStats;
  labels: PersonTaskLabel[];
}

export async function getPersonTasks(id: string): Promise<PersonTasksResult> {
  const res = await apiFetch<Envelope<PersonTasksResult>>(`/api/people/${id}/tasks`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Person activity log (CLE-241) -----------------------------------------
// Mirrors backend src/lib/personActivity.ts.

export interface PersonActivityTaskRef {
  id: string;
  identifier: string | null;
  title: string;
  projectId: string | null;
  projectName: string | null;
  projectCode: string | null;
}

export interface PersonActivityItem {
  id: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  oldDisplay: string | null;
  newDisplay: string | null;
  createdAt: string;
  user: { id: string; name: string } | null;
  task: PersonActivityTaskRef;
}

export interface PersonActivityResult {
  source: 'local';
  items: PersonActivityItem[];
}

export async function getPersonActivity(id: string): Promise<PersonActivityResult> {
  const res = await apiFetch<Envelope<PersonActivityResult>>(`/api/people/${id}/activity`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Org chart (CLE-70) ----------------------------------------------------

/** GET /api/people/org-chart — the hierarchical reporting structure. */
export async function getOrgChart(): Promise<OrgChart> {
  const res = await apiFetch<Envelope<OrgChart>>('/api/people/org-chart', {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * PATCH /api/people/:id/manager — set or clear a person's superior (admin-only).
 * Pass `null` to detach the person to the top level.
 */
export async function setPersonManager(id: string, managerId: string | null): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}/manager`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ managerId }),
  });
  return res.data;
}

/**
 * PATCH /api/people/:id/department — move a person into a department or clear
 * it with `null` (people.canEdit). The backend keeps the legacy `department`
 * name mirror in sync.
 */
export async function setPersonDepartment(
  id: string,
  departmentId: string | null,
): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}/department`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ departmentId }),
  });
  return res.data;
}

// --- Person contact + linked account editing --------------------------------

/** PATCH /api/people/:id/contact payload — only the provided keys are changed. */
export interface UpdatePersonContactInput {
  fullName?: string;
  email?: string;
  phone?: string | null;
  location?: string | null;
  departmentId?: string | null;
  title?: string | null;
  /** Weekly available hours (0-168). */
  capacity?: number;
  /** First working day (ISO date), null clears it. */
  startDate?: string | null;
  isActive?: boolean;
}

/**
 * PATCH /api/people/:id/contact — edit the person's profile (name, contact,
 * department, title, capacity, start date, active flag) (people.canEdit).
 * Returns the refreshed person detail.
 */
export async function updatePersonContact(
  id: string,
  input: UpdatePersonContactInput,
): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}/contact`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * DELETE /api/people/:id — soft-delete a team member (people.canManageRoles).
 * The Person row is preserved but marked inactive and any linked login account
 * is deactivated so they can no longer sign in.
 */
export async function deletePerson(id: string): Promise<Person | null> {
  const res = await apiFetch<Envelope<Person | null>>(`/api/people/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/people/:id/account payload (mirrors the deferred-account service). */
export interface CreatePersonAccountInput {
  accountMode?: 'invite' | 'password';
  password?: string;
  sendWelcomeEmail?: boolean;
  roleTemplateId?: string | null;
  customPermissions?: UserPermissionMatrix | null;
}

/** Response of POST /api/people/:id/account. */
export interface CreatePersonAccountResult {
  userId: string;
  /** Non-null only when no email was delivered — show it in a copy dialog. */
  generatedPassword: string | null;
  email: WelcomeEmailResult | null;
  invited: boolean;
  /** True when an existing unlinked account with the same email was linked instead of created. */
  linked?: boolean;
}

/**
 * POST /api/people/:id/account — provision a login account for a person
 * created without one (people.canManageRoles).
 */
export async function createPersonAccount(
  id: string,
  input: CreatePersonAccountInput,
): Promise<CreatePersonAccountResult> {
  const res = await apiFetch<Envelope<CreatePersonAccountResult>>(`/api/people/${id}/account`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** PATCH /api/people/:id/account payload — only the provided keys are changed. */
export interface UpdatePersonAccountInput {
  email?: string;
  name?: string;
  role?: UserRole;
  isActive?: boolean;
}

/**
 * PATCH /api/people/:id/account — edit the linked login account
 * (people.canManageRoles). Returns the refreshed person detail.
 */
export async function updatePersonAccount(
  id: string,
  input: UpdatePersonAccountInput,
): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}/account`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * POST /api/people/:id/verify-email — mark the linked account's email as
 * verified without the email round-trip (people.canManageRoles), for when the
 * verification message never arrived and login keeps refusing the account.
 * Returns the refreshed person detail.
 */
export async function verifyPersonEmail(id: string): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}/verify-email`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * PATCH /api/people/:id/services — replace the person's full set of service
 * links (services nomenclature; people.canEdit). Returns the person with the
 * refreshed `services` array.
 */
export async function updatePersonServices(id: string, serviceIds: string[]): Promise<Person> {
  const res = await apiFetch<Envelope<Person>>(`/api/people/${id}/services`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ serviceIds }),
  });
  return res.data;
}

// --- Person resources -------------------------------------------------------
// Company-level documents attached directly to the person (no linked auth
// account required). Mirrors the supplier resource flow with Romanian category
// folders matching the unified folder explorer UI.

export const PERSON_RESOURCE_CATEGORIES = ['Contracte', 'Facturi', 'Altele'] as const;
export type PersonResourceCategory = (typeof PERSON_RESOURCE_CATEGORIES)[number];

export interface PersonResource {
  id: string;
  personId: string;
  name: string;
  description: string | null;
  category: PersonResourceCategory;
  fileType: string | null;
  fileSize: number | null;
  s3Key: string;
  s3Url: string | null;
  uploadedAt: string;
}

export interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresIn?: number;
}

export async function listPersonResources(personId: string): Promise<PersonResource[]> {
  const res = await apiFetch<Envelope<PersonResource[]>>(`/api/people/${personId}/resources`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function requestPersonResourceUploadUrl(
  personId: string,
  input: {
    fileName: string;
    category: PersonResourceCategory;
    contentType: string;
    fileSize?: number;
  },
): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>(
    `/api/people/${personId}/resources/upload-url`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function createPersonResource(
  personId: string,
  input: {
    name: string;
    category: PersonResourceCategory;
    s3Key: string;
    description?: string;
    fileType?: string;
    fileSize?: number;
  },
): Promise<PersonResource> {
  const res = await apiFetch<Envelope<PersonResource>>(`/api/people/${personId}/resources`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deletePersonResource(resourceId: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/people/resources/${resourceId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}
